/* eslint-disable consistent-return */
import React, { useContext, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { io, Socket } from "socket.io-client";
import logger from "../../lib/logger";
import {
  useNfcScannerSettings,
  useSocketScannerSettings,
} from "../../lib/hooks/useQueryBoard";

interface ScannerContextValue {
  qrSocket: Socket | null;
  nfcSocket: Socket | null;
}

const ScannerContext = React.createContext<ScannerContextValue>({
  qrSocket: null,
  nfcSocket: null,
});

export const useScannerContext = () => useContext(ScannerContext);

interface ScannerProviderProps {
  children: React.ReactNode;
}

type SocketType = "qr" | "nfc";

const createSocket = (url: string, path = "/server/"): Socket => {
  return io(url, {
    path,
    reconnectionDelay: 5000,
    transports: ["websocket", "polling"],
    autoConnect: false,
  });
};

const setupSocketListeners = (
  socket: Socket,
  type: SocketType,
  handlers: {
    connect?: () => void;
    disconnect?: (reason: string) => void;
    error?: (err: Error) => void;
  } = {},
) => {
  const prefix = `[${type}-socket]`;

  socket.on("connect", () => {
    logger.debug(`${prefix} Connected`);
    handlers.connect?.();
  });

  socket.on("connect_error", (err) => {
    logger.error(`${prefix} Connection error:`, err);
    handlers.error?.(err);
    setTimeout(() => socket.connect(), 5000);
  });

  socket.on("disconnect", (reason) => {
    logger.debug(`${prefix} Disconnected:`, reason);
    handlers.disconnect?.(reason);
  });

  socket.io.on("reconnect", (attempt) =>
    logger.debug(`${prefix} Reconnected attempt: ${attempt}`),
  );

  socket.io.on("reconnect_attempt", (attempt) =>
    logger.debug(`${prefix} Reconnecting... attempt ${attempt}`),
  );
};

export const ScannerProvider = React.memo((props: ScannerProviderProps) => {
  const { children } = props;

  const {
    isSocketScannerActive,
    socketUrl: qrScannerSocketUrl,
    isSocketScannerAllowedForCheckin: isQrScannerAllowedForCheckin,
    isSocketScannerAllowedForCheckout: isQrScannerAllowedForCheckout,
  } = useSocketScannerSettings();

  const { isNfcScannerActive, nfcScannerSocketUrl } = useNfcScannerSettings();

  const navigate = useNavigate();

  const socketMapRef = useRef<Map<string, Socket>>(new Map());

  const getSocket = (url?: string) => {
    if (!url) {
      return null;
    }

    if (!socketMapRef.current.has(url)) {
      socketMapRef.current.set(url, createSocket(url));
    }

    return socketMapRef.current.get(url)!;
  };

  const qrSocket = isSocketScannerActive ? getSocket(qrScannerSocketUrl) : null;

  const nfcSocket =
    isNfcScannerActive && nfcScannerSocketUrl
      ? getSocket(nfcScannerSocketUrl)
      : null;

  useEffect(() => {
    if (!qrSocket) {
      return;
    }

    if (!qrSocket.connected && !(qrSocket as any).connecting) {
      (qrSocket as any).connecting = true;
      qrSocket.connect();
    }

    setupSocketListeners(qrSocket, "qr");

    const onScan = ({ code }: { code: string }) => {
      logger.debug(`[qr-socket] QR scanned: ${code}`);

      if (!(/^\d+$/.test(code) && code.length === 6)) {
        qrSocket.emit("scan-result", { result: "bad" });
        return;
      }

      let pathname = `/scan/${code}`;

      if (isQrScannerAllowedForCheckin && !isQrScannerAllowedForCheckout) {
        pathname = `/scan/${code}/checkin`;
      } else if (
        !isQrScannerAllowedForCheckin &&
        isQrScannerAllowedForCheckout
      ) {
        pathname = `/scan/${code}/checkout`;
      }

      navigate(pathname);
    };

    qrSocket.on("scan", onScan);

    return () => {
      qrSocket.off("scan", onScan);
    };
  }, [
    qrSocket,
    isQrScannerAllowedForCheckin,
    isQrScannerAllowedForCheckout,
    navigate,
  ]);

  useEffect(() => {
    if (!nfcSocket) {
      return;
    }

    if (!nfcSocket.connected && !(nfcSocket as any).connecting) {
      (nfcSocket as any).connecting = true;
      nfcSocket.connect();
    }

    setupSocketListeners(nfcSocket, "nfc");

    const onNfc = ({ uid }: { uid: string }) => {
      logger.debug(`[nfc-socket] NFC UID: ${uid}`);
      navigate(`/scan/${uid}?isNFC=true`);
    };

    nfcSocket.on("nfc-read", onNfc);

    return () => {
      nfcSocket.off("nfc-read", onNfc);
    };
  }, [nfcSocket, navigate]);

  const value = useMemo(() => ({ qrSocket, nfcSocket }), [qrSocket, nfcSocket]);

  return (
    <ScannerContext.Provider value={value}>{children}</ScannerContext.Provider>
  );
});

export default ScannerProvider;
